import React, { useState } from 'react';
import OpenAI from 'openai';

interface ApiKeySectionProps {
  onApiKeySubmit?: (apiKey: string) => void;
  onDisconnect?: () => void;
  isConnected?: boolean;
  loadedFromCookie?: boolean;
}

const ApiKeySection: React.FC<ApiKeySectionProps> = ({ 
  onApiKeySubmit, 
  onDisconnect,
  isConnected = false,
  loadedFromCookie = false
}) => {
  const [apiKey, setApiKey] = useState('');
  const [connectionStatus, setConnectionStatus] = useState<'idle' | 'validating' | 'success' | 'error'>('idle');
  const [statusMessage, setStatusMessage] = useState('');

  // Validate OpenAI API key format
  const validateApiKey = (key: string): { isValid: boolean; message: string } => {
    if (!key.trim()) {
      return { isValid: false, message: 'API key is required' };
    }
    
    if (!key.startsWith('sk-')) {
      return { isValid: false, message: 'API key must start with "sk-"' };
    }
    
    if (key.length < 20) {
      return { isValid: false, message: 'API key appears to be too short' };
    }
    
    if (key.length > 200) {
      return { isValid: false, message: 'API key appears to be too long' };
    }
    
    return { isValid: true, message: 'Valid API key format' };
  };

  // Validate API key against OpenAI servers
  const validateApiKeyWithOpenAI = async (key: string): Promise<{ isValid: boolean; message: string }> => {
    try {
      const openai = new OpenAI({
        apiKey: key,
        dangerouslyAllowBrowser: true // Note: In production, API calls should be made from backend
      });

      // Make a simple API call to validate the key
      await openai.models.list();
      
      return { isValid: true, message: 'API key validated successfully' };
    } catch (error: any) {
      console.error('API key validation error:', error);
      
      if (error?.status === 401) {
        return { isValid: false, message: 'Invalid API key. Please check your key and try again.' };
      } else if (error?.status === 429) {
        return { isValid: false, message: 'Rate limit exceeded. Please try again later.' };
      } else if (error?.status === 403) {
        return { isValid: false, message: 'API key does not have required permissions.' };
      } else if (error?.code === 'insufficient_quota') {
        return { isValid: false, message: 'API key has insufficient quota. Please check your OpenAI billing.' };
      } else {
        return { isValid: false, message: 'Failed to validate API key. Please check your connection and try again.' };
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const validation = validateApiKey(apiKey);
    
    if (!validation.isValid) {
      setConnectionStatus('error');
      setStatusMessage(validation.message);
      return;
    }

    setConnectionStatus('validating');
    setStatusMessage('Validating API key with OpenAI...');
    
    try {
      const apiValidation = await validateApiKeyWithOpenAI(apiKey);
      
      if (apiValidation.isValid) {
        setConnectionStatus('success');
        setStatusMessage('API key connected successfully');
        onApiKeySubmit?.(apiKey);
      } else {
        setConnectionStatus('error');
        setStatusMessage(apiValidation.message);
      }
    } catch (error) {
      setConnectionStatus('error');
      setStatusMessage('Unexpected error occurred during validation');
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setApiKey(e.target.value);
    // Reset status when user starts typing
    if (connectionStatus === 'error') {
      setConnectionStatus('idle');
      setStatusMessage('');
    }
  };

  const handleDisconnect = () => {
    setApiKey('');
    setConnectionStatus('idle');
    setStatusMessage('');
    onDisconnect?.();
  };

  const isInputEmpty = !apiKey.trim();
  const isProcessing = connectionStatus === 'validating';

  return (
    <section className="modern-api-key-section">
      <div className="api-key-card-modern">
        <div className="card-header">
          <div className="connection-icon">
            {isConnected ? (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            ) : (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M13 10V3L4 14h7v7l9-11h-7z" fill="currentColor"/>
              </svg>
            )}
          </div>
          <div className="card-title-group">
            <h3 className="modern-card-title">
              {isConnected ? 'Connected to OpenAI' : 'Connect Your OpenAI API Key'}
            </h3>
            <p className="modern-card-subtitle">
              {isConnected 
                ? loadedFromCookie 
                  ? 'Your saved API key has been automatically loaded and is ready to explore'
                  : 'Your API key is securely connected and ready to explore'
                : 'Enter your API key to start exploring AI-powered conversations'
              }
            </p>
          </div>
        </div>
        
        {!isConnected ? (
          <form onSubmit={handleSubmit} className="modern-api-key-form">
            <div className="modern-input-group">
              <div className="input-container">
                <div className="input-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M15.75 8.25a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <input
                  type="password"
                  value={apiKey}
                  onChange={handleInputChange}
                  placeholder="sk-..."
                  className="modern-api-key-input"
                  disabled={isProcessing}
                />
                <div className="input-border"></div>
              </div>
              <button 
                type="submit" 
                className={`modern-connect-button ${isProcessing ? 'processing' : ''}`}
                disabled={isInputEmpty || isProcessing}
              >
                <span className="button-content">
                  {isProcessing ? (
                    <>
                      <div className="loading-dots">
                        <div className="dot"></div>
                        <div className="dot"></div>
                        <div className="dot"></div>
                      </div>
                      <span>Validating...</span>
                    </>
                  ) : (
                    <>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M13 10V3L4 14h7v7l9-11h-7z" fill="currentColor"/>
                      </svg>
                      <span>Connect</span>
                    </>
                  )}
                </span>
                <div className="button-glow"></div>
              </button>
            </div>
            
            {/* Status message */}
            {statusMessage && (
              <div className={`modern-status-message ${connectionStatus}`}>
                <div className="status-icon">
                  {connectionStatus === 'success' && (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                  {connectionStatus === 'error' && (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                  {connectionStatus === 'validating' && (
                    <div className="validating-spinner"></div>
                  )}
                </div>
                <span>{statusMessage}</span>
              </div>
            )}
          </form>
        ) : (
          <div className="connected-state">
            <div className="connection-success">
              <div className="success-checkmark">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div className="success-text">
                <h4>{loadedFromCookie ? 'Automatically Connected!' : 'Successfully Connected!'}</h4>
                <p>{loadedFromCookie ? 'Your saved API key is validated and ready to use' : 'Your API key is validated and ready to use'}</p>
              </div>
            </div>
            <button 
              type="button" 
              onClick={handleDisconnect}
              className="modern-disconnect-button"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span>Disconnect</span>
            </button>
          </div>
        )}
        
        {/* Security note */}
        <div className="security-note">
          <div className="security-icon">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 1l3 3 3-3v6l-3 3-3-3V1zM9 12l3 3 3-3v6l-3 3-3-3v-6z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <span>Your API key is stored securely in cookies and never sent to our servers</span>
        </div>
      </div>
    </section>
  );
};

export default ApiKeySection; 